// @ts-nocheck
/**
 * LocationsService - Handles Shopify Storefront API communication and data management
 *
 * Responsibilities:
 * - Fetch locations from Shopify Storefront API
 * - Cache location data in sessionStorage (5-minute TTL)
 * - Transform GraphQL responses to standardized format
 * - Calculate store open/closed status
 * - Provide utility functions for formatting
 */

import { createTranslator } from './translator.js';

/**
 * Utility: Escape HTML to prevent XSS
 */
export function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Utility: Format time from 24h to 12h format
 */
export function formatTime(time) {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'pm' : 'am';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
}

/**
 * Utility: Format phone number based on country code
 */
export function formatPhoneNumber(phone, countryCode) {
  if (!phone) return '';

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Format based on country
  switch (countryCode) {
    case 'us':
    case 'ca':
      // Format as (XXX) XXX-XXXX
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (digits.length === 11 && digits[0] === '1') {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
      }
      break;
    case 'au':
      // Format as +61 X XXXX XXXX
      if (digits.length === 10 && digits.startsWith('0')) {
        return `+61 ${digits.slice(1, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
      } else if (digits.length === 11 && digits.startsWith('61')) {
        return `+${digits.slice(0, 2)} ${digits.slice(2, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
      }
      break;
  }

  return phone; // Return original if no formatting rules match
}

/**
 * Utility: Slugify a string for URL-safe format
 * @param {string} text - Text to slugify
 * @returns {string} Slugified text
 */
export function slugify(text) {
  if (!text) return '';

  return text
    .toString()
    .toLowerCase()
    .trim()
    // Remove apostrophes
    .replace(/[']/g, '')
    // Replace spaces and underscores with hyphens
    .replace(/[\s_]+/g, '-')
    // Remove all non-word chars except hyphens
    .replace(/[^\w\-]+/g, '')
    // Replace multiple hyphens with single hyphen
    .replace(/\-\-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+/, '')
    .replace(/-+$/, '');
}

/**
 * Utility: Convert Shopify Rich Text metafield schema to HTML
 * @param {Object|string} schema - The schema object or JSON string
 * @param {Object} options - Conversion options
 * @param {string|boolean} options.scoped - Scoped class name or boolean for default 'rte' class
 * @param {Object} options.classes - Tag-specific class names
 * @param {boolean} options.newLineToBreak - Convert \n to <br> tags
 * @returns {string} Converted HTML string
 */
export function convertRichTextToHtml(schema, options = {}) {
  if (!schema) return '';

  let { scoped, classes, newLineToBreak } = options;

  // Parse JSON string if needed
  if (typeof schema === 'string') {
    try {
      schema = JSON.parse(schema);
    } catch (e) {
      console.warn('Failed to parse rich text schema:', e);
      return '';
    }
  }

  // Handle root element
  if (schema.type === 'root' && schema.children) {
    const content = convertChildren(schema.children, options);
    if (scoped) {
      const className = scoped === true ? 'rte' : scoped;
      return `<div class="${escapeHtml(className)}">${content}</div>`;
    }
    return content;
  }

  // Handle array of elements
  if (Array.isArray(schema)) {
    return convertChildren(schema, options);
  }

  return '';
}

/**
 * Convert array of rich text elements to HTML
 * @private
 */
function convertChildren(children, options) {
  if (!children || !Array.isArray(children)) return '';

  return children.map(el => convertElement(el, options)).join('');
}

/**
 * Convert single rich text element to HTML
 * @private
 */
function convertElement(el, options) {
  if (!el || !el.type) return '';

  switch (el.type) {
    case 'paragraph':
      return buildParagraph(el, options);
    case 'heading':
      return buildHeading(el, options);
    case 'list':
      return buildList(el, options);
    case 'list-item':
      return buildListItem(el, options);
    case 'link':
      return buildLink(el, options);
    case 'text':
      return buildText(el, options);
    default:
      console.warn(`Unknown rich text element type: ${el.type}`);
      return '';
  }
}

/**
 * Build paragraph element
 * @private
 */
function buildParagraph(el, options) {
  const content = convertChildren(el.children, options);
  return createElement('p', options.classes, content);
}

/**
 * Build heading element
 * @private
 */
function buildHeading(el, options) {
  const tag = `h${el.level || 2}`;
  const content = convertChildren(el.children, options);
  return createElement(tag, options.classes, content);
}

/**
 * Build list element
 * @private
 */
function buildList(el, options) {
  const tag = el.listType === 'ordered' ? 'ol' : 'ul';
  const content = convertChildren(el.children, options);
  return createElement(tag, options.classes, content);
}

/**
 * Build list item element
 * @private
 */
function buildListItem(el, options) {
  const content = convertChildren(el.children, options);
  return createElement('li', options.classes, content);
}

/**
 * Build link element
 * @private
 */
function buildLink(el, options) {
  const content = convertChildren(el.children, options);
  const attributes = {};

  if (el.url) attributes.href = el.url;
  if (el.title) attributes.title = el.title;
  if (el.target) attributes.target = el.target;

  // Add rel="noopener noreferrer" for external links with target
  if (el.target === '_blank') {
    attributes.rel = 'noopener noreferrer';
  }

  return createElement('a', options.classes, content, attributes);
}

/**
 * Build text element with formatting
 * @private
 */
function buildText(el, options) {
  let value = el.value || '';

  // Convert newlines to <br> if requested
  if (options.newLineToBreak) {
    value = value.replace(/\n/g, '<br>');
  }

  // Escape HTML to prevent XSS
  value = escapeHtml(value);

  // Apply text formatting
  if (el.bold && el.italic) {
    return createElement('strong', options.classes, createElement('em', options.classes, value));
  } else if (el.bold) {
    return createElement('strong', options.classes, value);
  } else if (el.italic) {
    return createElement('em', options.classes, value);
  }

  return value;
}

/**
 * Create HTML element with class and attributes
 * @private
 */
function createElement(tag, classes, content, attributes = {}) {
  // Add class if specified for this tag
  if (classes && classes[tag]) {
    attributes.class = classes[tag];
  }

  // Build attributes string
  const attrs = Object.entries(attributes)
    .filter(([key, value]) => value != null && value !== '')
    .map(([key, value]) => `${key}="${escapeHtml(String(value))}"`)
    .join(' ');

  const attrsString = attrs ? ` ${attrs}` : '';

  return `<${tag}${attrsString}>${content}</${tag}>`;
}

/**
 * LocationsService Class
 * Manages all server-side communication and data transformation
 */
export class LocationsService {
  /**
   * Initialize the locations service
   * @param {Object} config - Service configuration
   * @param {string} config.shopDomain - Shopify shop domain
   * @param {string} config.apiVersion - Storefront API version
   * @param {string} config.accessToken - Storefront API access token
   * @param {Object} config.translator - Optional translator instance
   */
  constructor({ shopDomain, apiVersion, accessToken, defaultTimezone, translator = null }) {
    this.shopDomain = shopDomain;
    this.apiVersion = apiVersion;
    this.accessToken = accessToken;
    this.storefrontApiUrl = `https://${shopDomain}/api/${apiVersion}/graphql.json`;
    this.defaultTimezone = defaultTimezone || 'UTC';

    // Cache configuration
    // v2: invalidates pre-MF37 cached catalogs (entries without
    // mf37_bakery_clickcollect_status would hide every SSD result).
    // v3: invalidates pre-MF35 cached catalogs (entries without
    // mf35_location_deliverect_url would drop the Deliverect arrival URL
    // from new selections / cart attributes).
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes in milliseconds
    this.cacheKey = 'store_locator_locations_v3';
    this.cacheTimestampKey = 'store_locator_timestamp';

    // Use provided translator or create default one
    this.translator = translator || createTranslator('store-locator-translations', '');
  }

  /**
   * Fetch all locations live from the Storefront API.
   *
   * Bakery data — opening / pickup hours (MF03/MF04), closures (MF05), added / removed stores,
   * and the MF01 internal bakery id that drives storefront MF16/MF20 availability — changes
   * frequently. We deliberately do NOT read the static `locations-<brand>.json` asset: it is a
   * manual snapshot (regenerated only when someone runs the Hydrogen `/api/admin/sync-locations`
   * endpoint), so it silently goes stale and a stale catalog breaks availability (e.g. a snapshot
   * taken before MF01 was populated leaves every product showing "Unavailable"). The Storefront
   * API is always current. The result is still cached in sessionStorage for `cacheExpiry` so we
   * don't refetch on every navigation within a session.
   *
   * @returns {Promise<Array>} Array of transformed location objects
   */
  async getAllLocations() {
    // Try to use cached data first (sessionStorage, cacheExpiry TTL)
    const cachedData = this.getCachedData(this.cacheKey);
    if (cachedData) {
      // Recalculate status for each location since it's time-dependent
      return cachedData.map((location) => this.refreshOpeningAlignedLocation(location));
    }

    return await this.fetchFromStorefrontApi();
  }

  /**
   * Fetch locations from Storefront API (the single source of truth — see getAllLocations).
   * @private
   */
  async fetchFromStorefrontApi() {
    const allLocations = [];
    let hasMorePages = true;
    let currentCursor = null;

    // Fetch all pages using pagination
    while (hasMorePages) {
      const variables = {
        first: 250 // Fetch 250 locations at a time
      };

      if (currentCursor) {
        variables.after = currentCursor;
      }

      const response = await fetch(this.storefrontApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.accessToken
        },
        body: JSON.stringify({
          query: this.buildLocationsQuery(),
          variables
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors) {
        throw new Error(data.errors[0].message);
      }

      const locationsData = data.data.locations;
      const locations = this.transformLocations(locationsData.edges);

      // Update pagination info
      currentCursor = locationsData.pageInfo.endCursor;
      hasMorePages = locationsData.pageInfo.hasNextPage;

      // Append locations, deduplicating by ID
      const existingIds = new Set(allLocations.map(location => location.id));
      const newLocations = locations.filter(location => !existingIds.has(location.id));
      allLocations.push(...newLocations);
    }

    // Cache the results (without status, as it will be recalculated on read)
    const locationsToCache = allLocations.map(location => {
      const { status, ...locationWithoutStatus } = location;
      return locationWithoutStatus;
    });
    this.setCachedData(this.cacheKey, locationsToCache);

    return allLocations;
  }

  /**
   * Fetch single location by ID
   * Note: Shopify Storefront API doesn't support single location query,
   * so we fetch all locations and filter by ID
   * @param {string} id - Location ID (can be short "123" or full "gid://shopify/Location/123")
   * @returns {Promise<Object>} Transformed location object with fresh status
   */
  async getLocationById(id) {
    try {
      // Get all locations (uses cache if available, with recalculated status)
      const allLocations = await this.getAllLocations();

      // Convert short ID to full GID if needed
      const fullId = id.startsWith('gid://')
        ? id
        : `gid://shopify/Location/${id}`;

      // Find location by ID
      const location = allLocations.find(loc => loc.id === fullId);

      if (!location) {
        throw new Error(`Location not found: ${id}`);
      }

      return location;

    } catch (error) {
      console.error('Failed to load location by ID:', error);
      throw error;
    }
  }

  /**
   * Fetch single location by slug
   * @param {string} slug - Location slug (e.g., "warriewood-bakery")
   * @returns {Promise<Object>} Transformed location object with fresh status
   */
  async getLocationBySlug(slug) {
    try {
      // Get all locations (uses cache if available, with recalculated status)
      const allLocations = await this.getAllLocations();

      // Find location by slug
      const location = allLocations.find(loc => loc.slug === slug);

      if (!location) {
        throw new Error(`Location not found: ${slug}`);
      }

      return location;

    } catch (error) {
      console.error('Failed to load location by slug:', error);
      throw error;
    }
  }

  /**
   * Search locations by name (client-side filtering)
   * @param {string} query - Search query
   * @returns {Promise<Array>} Array of matching locations
   */
  async searchLocationsByName(query) {
    try {
      const allLocations = await this.getAllLocations();
      const queryLower = query.toLowerCase();

      return allLocations
        .filter(location => location.name.toLowerCase().includes(queryLower))
        .slice(0, 3); // Max 3 results for autocomplete

    } catch (error) {
      console.error('Store name search failed:', error);
      return [];
    }
  }

  /**
   * Build GraphQL query for fetching all locations from Storefront API
   * @private
   */
  buildLocationsQuery() {
    return `
      query getLocations($first: Int!, $after: String) {
        locations(first: $first, after: $after) {
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          edges {
            cursor
            node {
              id
              name
              address {
                address1
                address2
                city
                province
                provinceCode
                zip
                country
                countryCode
                phone
                latitude
                longitude
                formatted
              }
              metafields(identifiers: [
                {namespace: "amb", key: "public_store_name"},
                {namespace: "amb", key: "opening_hours_note"},
                {namespace: "amb", key: "delivery_service"},
                {namespace: "amb", key: "public_holiday_special_opening_hours"},
                {namespace: "amb", key: "temporary_closure"},
                {namespace: "amb", key: "description"},
                {namespace: "amb", key: "bakery_operator"},
                {namespace: "amb", key: "timezone"},
                {namespace: "amb", key: "in_store_pickup"},
                {namespace: "amb", key: "opening_hours"},
                {namespace: "custom", key: "mf03_bakery_opening_hours"},
                {namespace: "custom", key: "mf04_bakery_pickup_hours"},
                {namespace: "custom", key: "mf05_bakery_closures"},
                {namespace: "custom", key: "mf01_internal_bakery_id"},
                {namespace: "custom", key: "mf35_location_deliverect_url"},
                {namespace: "custom", key: "mf37_bakery_clickcollect_status"}
              ]) {
                key
                value
                type
              }
            }
          }
        }
      }
    `;
  }


  /**
   * Transform array of GraphQL location edges to standardized format
   * @private
   */
  transformLocations(edges) {
    return edges.map(edge => this.transformLocation(edge.node));
  }

  /**
   * Transform single GraphQL location node to standardized format
   * @private
   */
  transformLocation(node) {
    // Parse metafields into object
    const metafields = {};
    if (node.metafields && Array.isArray(node.metafields)) {
      node.metafields.forEach(mf => {
        if (!mf || !mf.key) return; // Skip null or invalid metafields

        let value = mf.value;
        // Parse JSON metafields
        if (mf.type === 'json' && value) {
          try {
            value = JSON.parse(value);
          } catch (e) {
            console.warn(`Failed to parse JSON metafield ${mf.key}:`, e);
          }
        }
        metafields[mf.key] = value;
      });
    }

    const storeName = metafields.public_store_name || node.name;

    const transformedMetafields = {
      openingHoursNote: metafields.opening_hours_note || null,
      deliveryService: metafields.delivery_service || null, // Fixed: Use singular key
      holidayOpeningHours: metafields.public_holiday_special_opening_hours || null,
      temporaryClosure: metafields.temporary_closure || null,
      description: metafields.description || null,
      bakeryOperator: metafields.bakery_operator || null,
      timezone: metafields.timezone || this.defaultTimezone
    };

    this.#attachRawCustomMetafieldSources(transformedMetafields, {
      mf03: metafields.mf03_bakery_opening_hours,
      mf04: metafields.mf04_bakery_pickup_hours,
      mf05: metafields.mf05_bakery_closures,
    });
    this.#applyHoursWeek(transformedMetafields, LocationsService.buildOpeningHoursWeek);

    const mf01Raw = metafields.mf01_internal_bakery_id ?? metafields.mf01InternalBakeryId;
    if (mf01Raw != null && String(mf01Raw).trim() !== '') {
      transformedMetafields.mf01_internal_bakery_id = String(mf01Raw).trim();
    }

    // MF35 Deliverect arrival URL — plain string metafield. Carried onto the
    // SSD selection (deliverectUrl) and synced as the `amb_deliverect_url`
    // cart attribute so the order-confirmation email can swap "View your
    // order" for "Notify staff you've arrived". Trimmed string or null.
    const mf35Raw = metafields.mf35_location_deliverect_url;
    transformedMetafields.mf35_location_deliverect_url =
      mf35Raw != null && String(mf35Raw).trim() !== '' ? String(mf35Raw).trim() : null;

    // MF37 click & collect status — boolean metafield, arrives as the string
    // "true"/"false" from the Storefront API. Normalised here so it survives
    // the sessionStorage cache round-trip. Missing/false both mean "not
    // click & collect enabled" (see isClickCollectEnabled).
    const mf37Raw = metafields.mf37_bakery_clickcollect_status;
    transformedMetafields.mf37_bakery_clickcollect_status =
      mf37Raw === true || mf37Raw === 'true';

    const transformedData = {
      id: node.id,
      name: storeName,
      slug: slugify(storeName),
      // Raw Shopify Location name (NOT the `amb.public_store_name` display
      // override). Checkout labels each native local-pickup option with this,
      // and it feeds the `amb_location_name` cart attribute. Checkout matching
      // itself now keys off the Location GID above via `amb_store_id`; this
      // stays for the superseded pickup-location-filter rollback path.
      locationName: node.name,
      address: {
        address1: node.address.address1,
        address2: node.address.address2,
        city: node.address.city,
        province: node.address.province,
        provinceCode: node.address.provinceCode,
        zip: node.address.zip,
        country: node.address.country,
        countryCode: node.address.countryCode,
        formatted: node.address.formatted
      },
      phone: node.address.phone,
      coordinates: {
        latitude: node.address.latitude,
        longitude: node.address.longitude
      },
      metafields: transformedMetafields,
    };

    // Catalog default is MF03 trading hours, which is what the Store Locator cards,
    // the map marker popups, and the Store Detail page advertise. The SSD re-derives
    // its own MF04 pickup view via refreshPickupAlignedLocation.
    transformedData.status = this.calculateOpeningStoreStatus(transformedData.metafields);

    return transformedData;
  }

  /**
   * Calculate store open/closed status from metafields
   * @param {Object} metafields - Store metafields object
   * @returns {Object} Status object with isOpen, isTemporarilyClosed, statusText, nextStateChangeText
   */
  calculateStoreStatus(metafields) {
    let isOpen = false;
    let isTemporarilyClosed = false;
    let statusText = 'Closed';
    let nextStateChange = null;

    // Get the bakery's timezone (fallback to default if not set)
    const timezone = metafields?.timezone || this.defaultTimezone;

    // Check temporary closure first
    if (metafields?.temporaryClosure?.temporarily_close_bakery) {
      const closure = metafields.temporaryClosure;
      const endDate = closure.temporary_close_end_date;
      
      // Check if end date has passed
      let closureStillActive = true;
      if (endDate && endDate !== 'null') {
        const endDateTime = this.parseDate(endDate, timezone);
        const now = this.getCurrentTime(timezone);
        
        // If end date exists and has passed, closure is no longer active
        if (endDateTime && now >= endDateTime) {
          closureStillActive = false;
        }
      }
      
      if (closureStillActive) {
        isTemporarilyClosed = true;
        statusText = closure.bakery_closure_reason || 'Temporarily Closed';

        // Parse end date for next state change (in bakery's timezone)
        if (endDate && endDate !== 'null') {
          nextStateChange = this.parseDate(endDate, timezone);
        }
      }
    }
    
    // Only check regular hours if not temporarily closed
    if (!isTemporarilyClosed) {
      // Get current time in the bakery's timezone
      const now = this.getCurrentTime(timezone);
      const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const todayKey = daysOfWeek[now.getDay()];
      const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

      // MF05 closures take precedence over holiday hours and regular hours for the matching date.
      const todaysClosure = this.getTodaysClosure(metafields?.closureDates, timezone);
      if (todaysClosure) {
        if (todaysClosure.isClosed) {
          statusText = todaysClosure.label || 'Closed';
          nextStateChange = this.calculateNextOpening(
            now,
            metafields?.openingHours,
            metafields?.holidayOpeningHours,
            metafields?.temporaryClosure,
            timezone,
            metafields?.closureDates
          );
        } else if (todaysClosure.open && todaysClosure.close) {
          isOpen = this.isTimeWithinHours(currentTime, todaysClosure.open, todaysClosure.close);
          statusText = todaysClosure.label || (isOpen ? 'Open' : 'Closed');

          if (isOpen) {
            nextStateChange = this.parseTimeToday(todaysClosure.close, timezone);
          } else if (this.timeToMinutes(currentTime) < this.timeToMinutes(todaysClosure.open)) {
            nextStateChange = this.parseTimeToday(todaysClosure.open, timezone);
          } else {
            nextStateChange = this.calculateNextOpening(
              now,
              metafields?.openingHours,
              metafields?.holidayOpeningHours,
              metafields?.temporaryClosure,
              timezone,
              metafields?.closureDates
            );
          }
        }
        // MF05 isClosed=false without open/close → data issue; leave closed (no MF04/MF03 fallback)
      } else {
        // Check holiday hours first
        const holidayHours = this.getTodaysHolidayHours(metafields?.holidayOpeningHours, timezone);
        if (holidayHours) {
          if (holidayHours.status === 'closed') {
            statusText = holidayHours.name;
            nextStateChange = this.calculateNextOpening(
              now,
              metafields?.openingHours,
              metafields?.holidayOpeningHours,
              metafields?.temporaryClosure,
              timezone,
              metafields?.closureDates
            );
          } else {
            isOpen = this.isTimeWithinHours(currentTime, holidayHours.open, holidayHours.close);
            statusText = holidayHours.name;
            if (isOpen && holidayHours.close) {
              nextStateChange = this.parseTimeToday(holidayHours.close, timezone);
            }
          }
        } else {
          // Check regular hours
          const regularHours = metafields?.openingHours?.[todayKey];
          if (!regularHours || regularHours.status === 'closed') {
            nextStateChange = this.calculateNextOpening(
              now,
              metafields?.openingHours,
              metafields?.holidayOpeningHours,
              metafields?.temporaryClosure,
              timezone,
              metafields?.closureDates
            );
          } else {
            isOpen = this.isTimeWithinHours(currentTime, regularHours.open, regularHours.close);
            statusText = isOpen ? 'Open' : 'Closed';

            // Calculate next state change
            if (isOpen && regularHours.close) {
              nextStateChange = this.parseTimeToday(regularHours.close, timezone);
            } else if (!isOpen) {
              if (regularHours.open && this.timeToMinutes(currentTime) < this.timeToMinutes(regularHours.open)) {
                nextStateChange = this.parseTimeToday(regularHours.open, timezone);
              } else {
                nextStateChange = this.calculateNextOpening(
                  now,
                  metafields?.openingHours,
                  metafields?.holidayOpeningHours,
                  metafields?.temporaryClosure,
                  timezone,
                  metafields?.closureDates
                );
              }
            }
          }
        }
      }
    }

    return {
      isOpen,
      isTemporarilyClosed,
      statusText,
      nextStateChangeData: this.formatNextStateChange(
        nextStateChange,
        isOpen,
        isTemporarilyClosed,
        metafields?.openingHours,
        timezone
      )
    };
  }

  /**
   * Format next state change into translation key and arguments
   * Returns an object with translation key and ALL possible args for maximum flexibility
   * @private
   * @param {Date} nextStateChange - Next state change date
   * @param {boolean} isOpen - Whether store is currently open
   * @param {boolean} isTemporarilyClosed - Whether store is temporarily closed
   * @param {Object} openingHours - Opening hours object with day schedules
   * @param {string} timezone - Bakery's timezone (IANA format)
   * @returns {Object} Object with { key: string, args: object } or null for UI to translate
   */
  formatNextStateChange(nextStateChange, isOpen, isTemporarilyClosed, openingHours, timezone) {
    if (!nextStateChange) return null;

    const changeDate = new Date(nextStateChange);
    const now = this.getCurrentTime(timezone);

    // Format the time in 24-hour format (e.g., "17:00")
    const timeHours = changeDate.getHours();
    const timeMinutes = changeDate.getMinutes();
    const time24h = `${timeHours.toString().padStart(2, '0')}:${timeMinutes.toString().padStart(2, '0')}`;

    // Format the time in 12-hour format with lowercase am/pm, no space (e.g., "5:00pm")
    const hour12 = timeHours % 12 || 12;
    const ampm = timeHours >= 12 ? 'pm' : 'am';
    const time12h = `${hour12}:${timeMinutes.toString().padStart(2, '0')}${ampm}`;

    // `changeDate` and `now` are "fake-local" Dates: getCurrentTime() /
    // parseTimeToday() / calculateNextOpening() build them from the bakery's
    // wall-clock components in the BROWSER's local timezone, so their local
    // getters (getFullYear / getMonth / getDate / getDay / getHours) already
    // read as bakery-local. We must NOT re-apply `{ timeZone }` here — that
    // converts the underlying instant a second time and, when the browser tz
    // differs from the bakery tz, shifts the date across a day boundary. That
    // double-shift is what made a bakery that opens later TODAY read as
    // "opens tomorrow".
    const localDateKey = (d) =>
      `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d
        .getDate()
        .toString()
        .padStart(2, '0')}`;

    // Today / tomorrow compared by bakery-local calendar date (local components).
    const isToday = localDateKey(changeDate) === localDateKey(now);
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    const isTomorrow = localDateKey(changeDate) === localDateKey(tomorrow);

    // Full / short weekday for the next change day, read from local components
    // (no `timeZone` — see note above).
    const dayIso = changeDate.toLocaleDateString('en-US', { weekday: 'long' });
    const dayIsoShort = changeDate.toLocaleDateString('en-US', { weekday: 'short' });

    // Determine human-friendly "day" value
    let dayText;
    if (isToday) {
      dayText = 'today';
    } else if (isTomorrow) {
      dayText = 'tomorrow';
    } else {
      dayText = dayIso;
    }

    // Get the opening hours for the next state change day (not today!)
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const nextStateDay = daysOfWeek[changeDate.getDay()];
    const nextStateDayHours = openingHours?.[nextStateDay];

    // Parse the hours for that day
    let hoursString = null;
    if (nextStateDayHours && nextStateDayHours.status !== 'closed' && nextStateDayHours.open && nextStateDayHours.close) {
      hoursString = `${nextStateDayHours.open}-${nextStateDayHours.close}`;
    }

    let openTime24h = '';
    let openTime12h = '';
    let closeTime24h = '';
    let closeTime12h = '';

    if (hoursString) {
      // Parse "09:00-17:00" format
      const [start, end] = hoursString.split('-');
      if (start && end) {
        const [startHour, startMin] = start.split(':').map(Number);
        const [endHour, endMin] = end.split(':').map(Number);

        // 24h format
        openTime24h = `${startHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`;
        closeTime24h = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;

        // 12h format with lowercase am/pm, no space
        const startHour12 = startHour % 12 || 12;
        const startAmpm = startHour >= 12 ? 'pm' : 'am';
        const endHour12 = endHour % 12 || 12;
        const endAmpm = endHour >= 12 ? 'pm' : 'am';

        openTime12h = `${startHour12}:${startMin.toString().padStart(2, '0')}${startAmpm}`;
        closeTime12h = `${endHour12}:${endMin.toString().padStart(2, '0')}${endAmpm}`;
      }
    }

    // Month / date from local components (no `timeZone` — see note above).
    const monthName = changeDate.toLocaleDateString('en-US', { month: 'long' });
    const dateNumber = changeDate.getDate();

    // Build comprehensive args object
    const args = {
      open_time: openTime24h || time24h,        // 24h format: "09:00"
      open_time_12h: openTime12h || time12h,    // 12h format: "9:00am"
      close_time: closeTime24h,                 // 24h format: "17:00"
      close_time_12h: closeTime12h,             // 12h format: "5:00pm"
      day_iso: dayIso,                          // Full day: "Monday"
      day_iso_short: dayIsoShort,               // Short day: "Mon"
      day: dayText,                             // "today", "tomorrow", or day name
      timezone: timezone,                       // Bakery timezone: "Australia/Sydney"
      month: monthName,                         // Full month name: "December"
      date: dateNumber                          // Date number: 31
    };

    let message_key = null;

    if (isTemporarilyClosed) {
      if (isToday) {
        message_key = 'location.status.reopens_today_at';
      } else if (isTomorrow) {
        message_key = 'location.status.reopens_tomorrow_at';
      } else {
        message_key = 'location.status.reopens_on_date_at';
      }
    } else if (isOpen) {
      message_key = 'location.status.closes_at';
    } else {
      if (isToday) {
        message_key = 'location.status.opens_today_at';
      } else if (isTomorrow) {
        message_key = 'location.status.opens_tomorrow_at';
      } else {
        message_key = 'location.status.opens_on_day_at';
      }
    }

    // Return object with key and args for UI to translate
    // This ensures translator is ready when translation happens
    return {
      key: message_key,
      args: args
    };
  }

  getTodaysClosure(closureDates, timezone) {
    if (!Array.isArray(closureDates) || closureDates.length === 0) return null;

    const today = this.getCurrentTime(timezone);
    const todayStr = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;

    for (const entry of closureDates) {
      if (entry && entry.date === todayStr) return entry;
    }
    return null;
  }

  /**
   * Get today's holiday hours if applicable
   * @private
   */
  getTodaysHolidayHours(holidayOpeningHours, timezone) {
    if (!holidayOpeningHours) return null;

    const today = this.getCurrentTime(timezone);
    const todayStr = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;

    for (const [holidayName, holidayData] of Object.entries(holidayOpeningHours)) {
      if (holidayData.date === todayStr) {
        return {
          ...holidayData,
          name: holidayName // Include the holiday name for display
        };
      }
    }

    return null;
  }

  /**
   * Calculate when the store will next open
   * Resolution priority per checked date: MF05 (closures override) -> holiday hours -> regular hours.
   * Takes into account temporary closures, holiday hours, MF05 closures and regular hours.
   * @private
   */
  calculateNextOpening(currentDate, openingHours, holidayOpeningHours, temporaryClosure, timezone, closureDates) {
    // If temporarily closed, return the end date of the closure (parsed in bakery's timezone)
    if (temporaryClosure?.temporarily_close_bakery) {
      const endDate = temporaryClosure.temporary_close_end_date;
      if (endDate && endDate !== 'null') {
        return this.parseDate(endDate, timezone);
      }
      return null;
    }

    if (!openingHours && !(Array.isArray(closureDates) && closureDates.length)) return null;

    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const currentDay = currentDate.getDay();

    // Check next 7 days for an opening time
    for (let i = 1; i <= 7; i++) {
      const nextDate = new Date(currentDate);
      nextDate.setDate(currentDate.getDate() + i);

      const nextDayIndex = (currentDay + i) % 7;
      const nextDayKey = daysOfWeek[nextDayIndex];
      const dateStr = `${nextDate.getFullYear()}-${(nextDate.getMonth() + 1).toString().padStart(2, '0')}-${nextDate.getDate().toString().padStart(2, '0')}`;

      // 1. MF05 closure override for this date
      if (Array.isArray(closureDates)) {
        const closure = closureDates.find((entry) => entry?.date === dateStr);
        if (closure) {
          if (closure.isClosed) {
            // explicitly closed, skip to next day
            continue;
          }
          if (closure.open) {
            const [hours, minutes] = closure.open.split(':').map(Number);
            nextDate.setHours(hours, minutes, 0, 0);
            return nextDate;
          }
          // closure entry without usable hours -> fall through to other sources
        }
      }

      // 2. Holiday hours for this date
      let holidayHours = null;
      if (holidayOpeningHours) {
        for (const [holidayName, holidayData] of Object.entries(holidayOpeningHours)) {
          if (holidayData.date === dateStr) {
            holidayHours = { ...holidayData, name: holidayName };
            break;
          }
        }
      }

      if (holidayHours) {
        if (holidayHours.status !== 'closed' && holidayHours.open) {
          const [hours, minutes] = holidayHours.open.split(':').map(Number);
          nextDate.setHours(hours, minutes, 0, 0);
          return nextDate;
        }
        // Holiday is closed, continue to next day
        continue;
      }

      // 3. Regular hours for this day
      const nextDayHours = openingHours?.[nextDayKey];
      if (nextDayHours && nextDayHours.open && (nextDayHours.status !== 'closed')) {
        const [hours, minutes] = nextDayHours.open.split(':').map(Number);
        nextDate.setHours(hours, minutes, 0, 0);

        return nextDate;
      }
    }

    return null;
  }

  /**
   * Check if current time is within business hours
   * @private
   */
  isTimeWithinHours(currentTime, openTime, closeTime) {
    if (!openTime || !closeTime) return false;

    const current = this.timeToMinutes(currentTime);
    const open = this.timeToMinutes(openTime);
    const close = this.timeToMinutes(closeTime);

    // Handle overnight hours (e.g., open until 2:00 AM)
    if (close < open) {
      return current >= open || current <= close;
    }

    return current >= open && current <= close;
  }

  /**
   * Convert time string (HH:MM) to minutes since midnight
   * @private
   */
  timeToMinutes(timeString) {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  }

  /**
   * Parse time string (HH:MM) to a Date object for today in the specified timezone
   * @private
   */
  parseTimeToday(timeString, timezone) {
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = this.getCurrentTime(timezone);
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  /**
   * Get current time in the specified timezone
   * @private
   */
  getCurrentTime(timezone) {
    const now = new Date();

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(now);
    const year = parts.find(p => p.type === 'year').value;
    const month = parts.find(p => p.type === 'month').value;
    const day = parts.find(p => p.type === 'day').value;
    const hour = parts.find(p => p.type === 'hour').value;
    const minute = parts.find(p => p.type === 'minute').value;
    const second = parts.find(p => p.type === 'second').value;

    return new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hour),
      parseInt(minute),
      parseInt(second)
    );
  }

  /**
   * Parse a date string (YYYY-MM-DD or ISO format) in the specified timezone
   * @private
   * @param {string} dateString - Date string to parse
   * @param {string} timezone - Timezone to parse the date in
   * @returns {Date} Date object representing the date at midnight in the specified timezone
   */
  parseDate(dateString, timezone) {
    if (!dateString) return null;

    // If it's an ISO datetime string with timezone info, use it directly
    if (dateString.includes('T') && (dateString.includes('Z') || dateString.includes('+'))) {
      return new Date(dateString);
    }

    // For date-only strings (YYYY-MM-DD), treat as midnight in the bakery's timezone
    // Parse the date components
    const [year, month, day] = dateString.split('-').map(Number);

    // Create a Date object representing midnight in the target timezone
    // Using Date constructor with components to ensure proper timezone handling
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  /**
   * Get cached data from sessionStorage
   * @private
   */
  getCachedData(key) {
    try {
      const cachedData = sessionStorage.getItem(key);
      const cachedTimestamp = sessionStorage.getItem(this.cacheTimestampKey);

      if (cachedData && cachedTimestamp) {
        const age = Date.now() - parseInt(cachedTimestamp, 10);
        if (age < this.cacheExpiry) {
          return JSON.parse(cachedData);
        }
      }
    } catch (e) {
      // Cache read failed, return null
      console.warn('Failed to read cache:', e);
    }

    return null;
  }

  /**
   * Set cached data in sessionStorage
   * @private
   */
  setCachedData(key, data) {
    try {
      sessionStorage.setItem(key, JSON.stringify(data));
      sessionStorage.setItem(this.cacheTimestampKey, Date.now().toString());
    } catch (e) {
      // Cache write failed (quota exceeded or disabled), continue without caching
      console.warn('Failed to write cache:', e);
    }
  }

  /**
   * Clear all cached data
   */
  clearCache() {
    try {
      sessionStorage.removeItem(this.cacheKey);
      sessionStorage.removeItem(this.cacheTimestampKey);
    } catch (e) {
      console.warn('Failed to clear cache:', e);
    }
  }

  /** @private Remove legacy amb opening_hours blobs so status never reads them. */
  #stripLegacyHoursFromMetafields(metafields) {
    if (!metafields || typeof metafields !== 'object') return;
    delete metafields.opening_hours;
    delete metafields.openingHours;
    delete metafields.pickupHours;
  }

  /** @private Normalized `openingHours` + `closureDates` for a given week builder. */
  #applyHoursWeek(metafields, buildWeek) {
    const timezone = metafields.timezone || this.defaultTimezone;
    if (!metafields.closureDates) {
      metafields.closureDates = LocationsService.parseClosureDates(
        metafields.mf05_bakery_closures
      );
    }
    metafields.openingHours = buildWeek(
      metafields,
      timezone,
      this.getCurrentTime(timezone)
    );
  }

  /**
   * Re-apply pickup-aligned (MF05 → MF04 → MF03) `openingHours` + fresh `status`.
   * Session cache rehydration and SSD rows.
   * @param {Object} location
   * @returns {Object}
   */
  refreshPickupAlignedLocation(location) {
    return this.#refreshLocation(
      location,
      LocationsService.buildPickupAlignedOpeningHoursWeek
    );
  }

  /**
   * Re-apply trading-hours (MF05 → MF03, no MF04) `openingHours` + fresh `status`.
   * Store Locator cards / map popups and the Store Detail page.
   * @param {Object} location
   * @returns {Object}
   */
  refreshOpeningAlignedLocation(location) {
    return this.#refreshLocation(location, LocationsService.buildOpeningHoursWeek);
  }

  /** @private */
  #refreshLocation(location, buildWeek) {
    if (!location || typeof location !== 'object') return location;
    const metafields =
      location.metafields && typeof location.metafields === 'object'
        ? { ...location.metafields }
        : {};
    this.#stripLegacyHoursFromMetafields(metafields);
    this.#applyHoursWeek(metafields, buildWeek);
    return {
      ...location,
      metafields,
      status: this.#calculateStatusForWeek(metafields, buildWeek),
    };
  }

  /**
   * Badge + next-status: `calculateStoreStatus` on the MF04-first week (no second isOpen path).
   * Pickup slots / SSD still use `resolveAvailabilityForDate` directly.
   * @param {Object} metafields
   * @returns {Object} `calculateStoreStatus` result
   */
  calculatePickupAlignedStoreStatus(metafields) {
    return this.#calculateStatusForWeek(
      metafields,
      LocationsService.buildPickupAlignedOpeningHoursWeek
    );
  }

  /**
   * Badge + next-status on the MF03 trading week — "Open"/"Closes at" on the
   * Store Locator and Store Detail describe the bakery's doors, not Click & Collect.
   * @param {Object} metafields
   * @returns {Object} `calculateStoreStatus` result
   */
  calculateOpeningStoreStatus(metafields) {
    return this.#calculateStatusForWeek(metafields, LocationsService.buildOpeningHoursWeek);
  }

  /** @private */
  #calculateStatusForWeek(metafields, buildWeek) {
    const timezone = metafields?.timezone || this.defaultTimezone;
    const now = this.getCurrentTime(timezone);
    const base = metafields && typeof metafields === 'object' ? { ...metafields } : {};
    this.#stripLegacyHoursFromMetafields(base);
    const mf05 = LocationsService.parseClosureDates(base.mf05_bakery_closures);
    if (mf05) base.closureDates = mf05;
    base.openingHours = buildWeek(metafields, timezone, now);
    base.holidayOpeningHours = null;

    return this.calculateStoreStatus(base);
  }

  /**
   * Expose Shopify `custom.mf03/mf04/mf05` payloads on `metafields`
   * @private
   */
  #attachRawCustomMetafieldSources(out, { mf03, mf04, mf05 }) {
    out.mf03_bakery_opening_hours = this.#cloneRawMetafieldPayload(mf03 ?? {});
    out.mf04_bakery_pickup_hours = this.#cloneRawMetafieldPayload(mf04 ?? {});
    out.mf05_bakery_closures = this.#cloneRawMetafieldPayload(mf05 ?? {});
  }

  /** @private */
  #cloneRawMetafieldPayload(value) {
    if (value === undefined || value === null) return {};
    try {
      if (typeof structuredClone === 'function') return structuredClone(value);
    } catch (_) {
      /* fall through */
    }
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (_) {
      return value;
    }
  }

  /**
   * Normalize MF04 (custom.mf04_bakery_pickup_hours) into a 7-day pickup hours object.
   * Expected input shape (per spec):
   *   { pickupHours: { monday: { isAvailable, open, close }, ... } }
   * Falsy / invalid inputs -> null. Days are normalized to lowercase keys.
   * @private
   */
  /**
   * MF04 `isAvailable` may arrive as boolean false or string "false" from JSON / APIs.
   * @private
   */
  #isPickupDayExplicitlyUnavailable(value) {
    return (
      value === false ||
      value === 'false' ||
      value === 0 ||
      value === '0'
    );
  }

  #extractPickupHours(raw) {
    if (!raw) return null;
    let value = raw;
    if (typeof value === 'string') {
      try { value = JSON.parse(value); } catch (_) { return null; }
    }
    const source = value?.pickupHours || value;
    if (!source || typeof source !== 'object') return null;

    const result = {};
    for (const [dayKey, dayValue] of Object.entries(source)) {
      if (!dayValue || typeof dayValue !== 'object') continue;
      const normalizedKey = String(dayKey).toLowerCase();
      const isAvailable = !this.#isPickupDayExplicitlyUnavailable(dayValue.isAvailable);
      result[normalizedKey] = {
        isAvailable,
        open: isAvailable && typeof dayValue.open === 'string' ? dayValue.open : null,
        close: isAvailable && typeof dayValue.close === 'string' ? dayValue.close : null
      };
    }
    return Object.keys(result).length ? result : null;
  }

  /**
   * Normalize MF03 / legacy opening hours to PascalCase day keys (Monday…Sunday) for status + drawer pickup fallback.
   * Accepts: { openingHours: { monday: { isOpen, open, close } } } or { monday: … } or legacy { Monday: { status, open, close } }.
   * @private
   */
  #extractOpeningHours(raw) {
    if (!raw) return null;
    let value = raw;
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value);
      } catch (_) {
        return null;
      }
    }
    const source = value?.openingHours && typeof value.openingHours === 'object' ? value.openingHours : value;
    if (!source || typeof source !== 'object') return null;

    const pascalByLower = {
      sunday: 'Sunday',
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday'
    };

    const result = {};
    const validPascal = new Set(Object.values(pascalByLower));
    for (const [dayKey, dayValue] of Object.entries(source)) {
      if (!dayValue || typeof dayValue !== 'object') continue;
      const lk = String(dayKey).toLowerCase();
      const outKey = pascalByLower[lk] || (validPascal.has(String(dayKey)) ? String(dayKey) : null);
      if (!outKey) continue;

      const open = typeof dayValue.open === 'string' ? dayValue.open : null;
      const close = typeof dayValue.close === 'string' ? dayValue.close : null;
      const closed =
        dayValue.status === 'closed' ||
        dayValue.isOpen === false ||
        dayValue.isOpen === 'false' ||
        dayValue.isOpen === 0 ||
        dayValue.isOpen === '0' ||
        dayValue.isAvailable === false ||
        dayValue.isAvailable === 'false' ||
        dayValue.isAvailable === 0 ||
        dayValue.isAvailable === '0';

      if (closed) {
        result[outKey] = { status: 'closed', open: null, close: null };
      } else if (open && close) {
        result[outKey] = { open, close, isOpen: dayValue.isOpen !== false };
      }
    }
    return Object.keys(result).length ? result : null;
  }

  /**
   * Normalize MF05 (custom.mf05_bakery_closures) into an array of closure entries keyed by ISO date.
   * Expected input shape (per spec):
   *   { closureDates: [ { date: 'YYYY-MM-DD', label, isClosed, open?, close? } ] }
   * @private
   */
  #extractClosureDates(raw) {
    return LocationsService.parseClosureDates(raw);
  }

  /**
   * Day map from `custom.mf04_bakery_pickup_hours` (nested `pickupHours` or flat). Empty/`{}` → null.
   * Moved from `store-selector-drawer.js` `#unwrapMf04PickupDayMap` (logic unchanged).
   * @param {unknown} raw
   * @returns {Object|null}
   */
  static unwrapMf04PickupDayMap(raw) {
    if (raw == null || typeof raw !== 'object') return null;
    const map =
      raw.pickupHours && typeof raw.pickupHours === 'object' ? raw.pickupHours : raw;
    if (!map || typeof map !== 'object' || Object.keys(map).length === 0) return null;
    return map;
  }

  /**
   * Day map from `custom.mf03_bakery_opening_hours` (nested `openingHours` or flat). Empty/`{}` → null.
   * Moved from `store-selector-drawer.js` `#unwrapMf03OpeningDayMap` (logic unchanged).
   * @param {unknown} raw
   * @returns {Object|null}
   */
  static unwrapMf03OpeningDayMap(raw) {
    if (raw == null || typeof raw !== 'object') return null;
    const map =
      raw.openingHours && typeof raw.openingHours === 'object' ? raw.openingHours : raw;
    if (!map || typeof map !== 'object' || Object.keys(map).length === 0) return null;
    return map;
  }

  /**
   * The closed markers MF03 and MF04 share, so the two payloads can never drift
   * apart on what "shut for the day" looks like. Shopify hands JSON metafield
   * booleans back as booleans, strings, or numbers depending on how the row was
   * written, hence the widened comparisons.
   * @private
   * @param {Object} row weekday row from either payload
   * @returns {boolean}
   */
  static #isClosedDayRow(row) {
    const isFalseish = (value) =>
      value === false || value === 'false' || value === 0 || value === '0';
    return row.status === 'closed' || isFalseish(row.isOpen) || isFalseish(row.isAvailable);
  }

  /**
   * Trading window from **`metafields.mf03_bakery_opening_hours`** only
   * (not the normalized `openingHours` week this helps build).
   * `status: 'closed'` / `isOpen: false` / `isAvailable: false` (and string / number
   * equivalents) → closed, as does a missing payload, a missing weekday row, or a
   * row without both `open` and `close`.
   * MF03 is read here and only here: Store Locator badges, map popups, and the
   * Store Detail hours table. Pickup no longer reaches MF03 — see
   * `getPickupDayWindow`.
   * @param {Object} metafields
   * @param {string} dow lowercase weekday (`monday` …)
   * @param {string} pascalDay Pascal weekday (`Monday` …)
   * @returns {{ openStr: string, closeStr: string, source: 'mf03' } | null}
   */
  static getOpeningDayWindow(metafields, dow, pascalDay) {
    const openingMap = LocationsService.unwrapMf03OpeningDayMap(
      metafields?.mf03_bakery_opening_hours
    );
    if (!openingMap) return null;

    const ohRow = openingMap[dow] ?? openingMap[pascalDay];
    if (!ohRow || typeof ohRow !== 'object') return null;
    if (LocationsService.#isClosedDayRow(ohRow)) return null;

    const oStr = typeof ohRow.open === 'string' ? ohRow.open.trim() : '';
    const cStr = typeof ohRow.close === 'string' ? ohRow.close.trim() : '';
    if (!oStr || !cStr) return null;

    return { openStr: oStr, closeStr: cStr, source: 'mf03' };
  }

  /**
   * Pickup window from **`metafields.mf04_bakery_pickup_hours`** only
   * (not the normalized `pickupHours` map). `status: 'closed'` / `isAvailable: false`
   * / `isOpen: false` (and string / number equivalents) → closed, as does a missing
   * payload, a missing weekday row, or a row without both `open` and `close`.
   *
   * **There is deliberately no MF03 fallback.** Trading hours answer "are the doors
   * open?", which is a different question from "can I collect an order?", so a
   * bakery with no MF04 row for the day offers no pickup rather than silently
   * advertising its shop-front hours as collection windows. The consequence is
   * operational: MF04 must be populated for every Click & Collect bakery, or the
   * drawer will offer it no slots.
   * @param {Object} metafields
   * @param {string} dow lowercase weekday (`monday` …)
   * @param {string} pascalDay Pascal weekday (`Monday` …)
   * @returns {{ openStr: string, closeStr: string, source: 'mf04' } | null}
   */
  static getPickupDayWindow(metafields, dow, pascalDay) {
    const pickupMap = LocationsService.unwrapMf04PickupDayMap(
      metafields?.mf04_bakery_pickup_hours
    );
    if (!pickupMap) return null;

    const phRow = pickupMap[dow] ?? pickupMap[pascalDay];
    if (!phRow || typeof phRow !== 'object') return null;
    if (LocationsService.#isClosedDayRow(phRow)) return null;

    const pOpen = typeof phRow.open === 'string' ? phRow.open.trim() : '';
    const pClose = typeof phRow.close === 'string' ? phRow.close.trim() : '';
    if (!pOpen || !pClose) return null;

    return { openStr: pOpen, closeStr: pClose, source: 'mf04' };
  }

  /**
   * Click & Collect hours for a date — the Store Selector Drawer's only entry point.
   * 1) MF05 date closure — full close, reduced hours as final window, or malformed (always first).
   *    `isClosed: false` without `open`/`close` → data issue → closed (`mf05-malformed`).
   * 2) Else MF04 (`mf04_bakery_pickup_hours`) only. An empty payload, a missing
   *    weekday row, or a row marked closed all mean no pickup — MF03 trading hours
   *    are never consulted (see `getPickupDayWindow`).
   * @param {Object} metafields
   * @param {Date} date bakery-local calendar date
   * @returns {{ available: boolean, open: string|null, close: string|null, source: string }}
   */
  static resolveAvailabilityForDate(metafields, date) {
    return LocationsService.#resolveForDate(
      metafields,
      date,
      LocationsService.getPickupDayWindow
    );
  }

  /**
   * Trading hours for a date: MF05 date closure first (identical precedence to
   * `resolveAvailabilityForDate`), then MF03 only — MF04 pickup hours are never
   * consulted, and the reverse holds too. Backs the Store Locator badge and the Store Detail hours table,
   * which advertise when the bakery is open rather than when Click & Collect runs.
   * @param {Object} metafields
   * @param {Date} date bakery-local calendar date
   * @returns {{ available: boolean, open: string|null, close: string|null, source: string }}
   */
  static resolveOpeningForDate(metafields, date) {
    return LocationsService.#resolveForDate(
      metafields,
      date,
      LocationsService.getOpeningDayWindow
    );
  }

  /**
   * MF05 date closure → weekday window from `getWindow`.
   * MF05 `isClosed: false` without `open`/`close` is a data issue and stays closed
   * (`mf05-malformed`) rather than falling through to the weekday window.
   * @private
   * @param {Object} metafields
   * @param {Date} date
   * @param {(metafields: Object, dow: string, pascalDay: string) => ({ openStr: string, closeStr: string, source: string }|null)} getWindow
   */
  static #resolveForDate(metafields, date, getWindow) {
    const empty = { available: false, open: null, close: null, source: 'none' };
    if (!metafields || !(date instanceof Date) || Number.isNaN(date.getTime())) return empty;

    const dateStr = LocationsService.toIsoDate(date);
    const dow = LocationsService.getLowercaseDayKey(date);
    const pascalDay = LocationsService.getPascalDayKey(date);

    const closures = LocationsService.parseClosureDates(metafields.mf05_bakery_closures);
    if (Array.isArray(closures)) {
      const closure = closures.find((entry) => entry?.date === dateStr);
      if (closure) {
        const fullyClosed =
          closure.isClosed === true ||
          closure.isClosed === 'true' ||
          closure.isClosed === 1 ||
          closure.isClosed === '1';
        if (fullyClosed) return { ...empty, source: 'mf05' };

        const co = closure.open != null ? String(closure.open).trim() : '';
        const cc = closure.close != null ? String(closure.close).trim() : '';
        if (co && cc) {
          return { available: true, open: co, close: cc, source: 'mf05-reduced' };
        }
        return { ...empty, source: 'mf05-malformed' };
      }
    }

    const w = getWindow(metafields, dow, pascalDay);
    if (!w) return { ...empty, source: 'none' };

    return {
      available: true,
      open: w.openStr,
      close: w.closeStr,
      source: w.source
    };
  }

  /**
   * PascalCase `openingHours` for `calculateStoreStatus` / `calculateNextOpening`.
   * Each weekday uses the next occurrence’s window from `resolveAvailabilityForDate`
   * (MF05 → MF04). Caller passes bakery-local `now` from `getCurrentTime(timezone)`.
   *
   * Unlike `buildOpeningHoursWeek` this always returns an object, never `null`: it
   * feeds only the SSD's row badge, which has no "unknown" state, and a bakery with
   * no MF04 genuinely has no pickup on any day. It must not be used to publish
   * hours — Store Detail's table and JSON-LD take the trading week.
   * @param {Object} metafields
   * @param {string} timezone
   * @param {Date} now bakery-local “now”
   * @returns {Object} PascalCase keys (`Monday` … `Sunday`)
   */
  static buildPickupAlignedOpeningHoursWeek(metafields, timezone, now) {
    return LocationsService.#buildHoursWeek(
      metafields,
      now,
      LocationsService.resolveAvailabilityForDate
    );
  }

  /**
   * PascalCase `openingHours` built from MF05 → MF03 only (no MF04), for the
   * Store Locator badge and the Store Detail hours table / JSON-LD.
   *
   * Returns `null` when the bakery has no MF03 payload at all, which is what makes
   * Store Detail render "Hours not available" and omit `openingHoursSpecification`
   * from its JSON-LD. Building the week anyway would publish seven fabricated
   * "Closed" rows for what is really missing data. A bakery whose MF03 exists but
   * marks every day closed still gets a real week — that one is a deliberate answer.
   * @param {Object} metafields
   * @param {string} timezone
   * @param {Date} now bakery-local “now”
   * @returns {Object|null} PascalCase keys (`Monday` … `Sunday`), or `null`
   */
  static buildOpeningHoursWeek(metafields, timezone, now) {
    const openingMap = LocationsService.unwrapMf03OpeningDayMap(
      metafields?.mf03_bakery_opening_hours
    );
    if (!openingMap) return null;

    return LocationsService.#buildHoursWeek(
      metafields,
      now,
      LocationsService.resolveOpeningForDate
    );
  }

  /**
   * Each weekday takes the next occurrence of that day, so a date-specific MF05
   * closure lands on the weekday it actually falls on.
   * @private
   * @param {Object} metafields
   * @param {Date} now bakery-local “now”
   * @param {(metafields: Object, date: Date) => ({ available: boolean, open: string|null, close: string|null })} resolveForDate
   */
  static #buildHoursWeek(metafields, now, resolveForDate) {
    const pairs = [
      ['sunday', 'Sunday'],
      ['monday', 'Monday'],
      ['tuesday', 'Tuesday'],
      ['wednesday', 'Wednesday'],
      ['thursday', 'Thursday'],
      ['friday', 'Friday'],
      ['saturday', 'Saturday']
    ];
    const current =
      now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
    const hours = {};
    for (const [dow, pascal] of pairs) {
      const date = LocationsService.nextCalendarDateForWeekday(current, dow);
      const avail = resolveForDate(metafields, date);
      if (avail.available && avail.open && avail.close) {
        hours[pascal] = { open: avail.open, close: avail.close };
      } else {
        hours[pascal] = { status: 'closed', open: null, close: null };
      }
    }
    return hours;
  }

  /** @param {Date} date Moved from `store-selector-drawer.js` `#toIsoDate` */
  static toIsoDate(date) {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /** @param {Date} date Moved from `store-selector-drawer.js` `#getLowercaseDayKey` */
  static getLowercaseDayKey(date) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[date.getDay()];
  }

  /** @param {Date} date Moved from `store-selector-drawer.js` `#getPascalDayKey` */
  static getPascalDayKey(date) {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
  }

  /**
   * Next calendar date on or after `fromDate` matching lowercase weekday (`sunday` … `saturday`).
   * Moved from `store-selector-drawer.js` `#nextCalendarDateForWeekday`.
   * @param {Date} fromDate
   * @param {string} targetDowLower
   * @returns {Date}
   */
  static nextCalendarDateForWeekday(fromDate, targetDowLower) {
    const base = new Date(fromDate);
    base.setHours(0, 0, 0, 0);
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const targetIndex = days.indexOf(targetDowLower);
    if (targetIndex < 0) return base;
    for (let i = 0; i < 7; i++) {
      const candidate = new Date(base);
      candidate.setDate(base.getDate() + i);
      if (candidate.getDay() === targetIndex) return candidate;
    }
    return base;
  }

  /**
   * Normalize MF05 (`custom.mf05_bakery_closures`) for status + SSD pickup.
   * @param {unknown} raw
   * @returns {Array<{ date: string, label: string, isClosed: boolean, open: string|null, close: string|null }>|null}
   */
  static parseClosureDates(raw) {
    if (!raw) return null;
    let value = raw;
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value);
      } catch (_) {
        return null;
      }
    }
    const list = Array.isArray(value?.closureDates)
      ? value.closureDates
      : Array.isArray(value)
        ? value
        : null;
    if (!list || !list.length) return null;

    const result = [];
    for (const entry of list) {
      if (!entry || typeof entry !== 'object') continue;
      const date = typeof entry.date === 'string' ? entry.date.trim() : null;
      if (!date) continue;
      const isClosed =
        entry.isClosed === true ||
        entry.isClosed === 'true' ||
        entry.isClosed === 1 ||
        entry.isClosed === '1';
      result.push({
        date,
        label: typeof entry.label === 'string' ? entry.label : '',
        isClosed,
        open: !isClosed && typeof entry.open === 'string' ? entry.open.trim() : null,
        close: !isClosed && typeof entry.close === 'string' ? entry.close.trim() : null,
      });
    }
    return result.length ? result : null;
  }

  /**
   * Extract short ID from full Shopify GID
   * @param {string} gid - Full GID (e.g., "gid://shopify/Location/80049832128")
   * @returns {string} Short ID (e.g., "80049832128")
   */
  static extractShortId(gid) {
    if (!gid) return '';

    // If already short ID, return as-is
    if (!gid.startsWith('gid://')) {
      return gid;
    }

    // Extract numeric ID from GID format
    const match = gid.match(/gid:\/\/shopify\/Location\/(\d+)/);
    return match ? match[1] : gid;
  }

  /**
   * Internal bakery id from `custom.mf01_internal_bakery_id` (Storefront / static JSON).
   * Used to match MF16/MF20 product metafields — not the Shopify Location id.
   * @param {object|null|undefined} location
   * @returns {string}
   */
  static extractInternalBakeryId(location) {
    const mf = location?.metafields;
    if (!mf || typeof mf !== 'object') return '';
    const raw = mf.mf01_internal_bakery_id ?? mf.mf01InternalBakeryId;
    if (raw == null) return '';
    return String(raw).trim();
  }

  /**
   * Whether the location offers Click & Collect (`custom.mf37_bakery_clickcollect_status`).
   * Only explicit true enables it — missing or false both mean "not enabled".
   * Gates the store selector drawer list and the "Save as my bakery" buttons
   * on the store locator / store detail pages.
   * @param {object|null|undefined} location
   * @returns {boolean}
   */
  static isClickCollectEnabled(location) {
    const raw = location?.metafields?.mf37_bakery_clickcollect_status;
    return raw === true || raw === 'true';
  }
}

